import type {
  RecruitingSiteResponse,
  SiteJobDescriptionPublicControllerApiGetSiteJobDescriptionDetailForPublicRequest,
  SiteJobDescriptionPublicDetailResponse,
  SitePublicControllerApiGetRecruitingSiteRequest,
  SitePublicControllerApiGetSubdomainByUrlRootPathRequest,
} from '@flex-apis/recruiting';
import {
  getRecruitingSiteRemote,
  getSiteJobDescriptionDetailForPublicRemote,
  getSubdomainByUrlRootPathRemote,
} from '@flex-apis/recruiting';
import { isAxiosError } from '@flex-packages/error';
import type { AxiosInstance } from 'axios';

import { ErrorSubdomain } from '../../constants';

import { createSiteUnpublishedError } from './assert-published-site';
import { ErrorPage, NotFoundError } from './errors';

export async function fetchRecruitingSite(
  axiosInstance: AxiosInstance,
  param: SitePublicControllerApiGetRecruitingSiteRequest
): Promise<RecruitingSiteResponse> {
  const [getRecruitingSiteRaw] = getRecruitingSiteRemote(axiosInstance);

  try {
    const { data } = await getRecruitingSiteRaw(param);
    return data;
  } catch (error) {
    if (!isAxiosError(error)) {
      throw error;
    }

    switch (error.response?.data.code) {
      case 'SITE_404_000':
        throw new NotFoundError('사이트를 찾을 수 없습니다.');
      case 'SITE_404_001':
        throw createSiteUnpublishedError();
      default:
        throw error;
    }
  }
}

export async function fetchJobDescriptionForPublic(
  axiosInstance: AxiosInstance,
  param: SiteJobDescriptionPublicControllerApiGetSiteJobDescriptionDetailForPublicRequest
): Promise<SiteJobDescriptionPublicDetailResponse> {
  const [getJobDescriptionForPublicRaw] =
    getSiteJobDescriptionDetailForPublicRemote(axiosInstance);

  try {
    const { data } = await getJobDescriptionForPublicRaw(param);
    return data;
  } catch (error) {
    if (isNotFoundAxiosError(error)) {
      throw new ErrorPage(
        '공고가 없거나, 비공개 혹은 삭제처리 됐습니다.',
        ErrorSubdomain.NotFoundSubdomain
      );
    }
    throw error;
  }
}

export async function fetchSubstituteSubdomain(
  axiosInstance: AxiosInstance,
  param: SitePublicControllerApiGetSubdomainByUrlRootPathRequest
): Promise<string> {
  const { urlRootPath } = param;
  const [getSubdomainByUrlRootPath] =
    getSubdomainByUrlRootPathRemote(axiosInstance);

  try {
    const { data } = await getSubdomainByUrlRootPath(param);
    return data.subdomain || urlRootPath;
  } catch (error) {
    if (isNotFoundAxiosError(error)) {
      return urlRootPath;
    }
    throw error;
  }
}

function isNotFoundAxiosError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 404;
  }
  return false;
}
